import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPublicCaptionLanguageDisplayNames,
  hasRemoteCaptionSpeech,
  MULTILINGUAL_SPEAKER_LANGUAGE,
  SPEAKER_CAPTION_LANGUAGES,
  TARGET_CAPTION_LANGUAGES,
  type PublicCaptionLanguage,
  type PublicTargetCaptionLanguage,
} from "@kaleo/captions-core/languages";
import { fetchAppRequest } from "../lib/app-request";
import {
  formatDocsMessage,
  getLocalizedLanguageName,
  type DocsMessages,
  WORKER_ORIGIN,
} from "../lib/localization";
import {
  parseSpeakerLanguageCatalog,
  parseTargetLanguageCatalog,
} from "../lib/language-catalog";
import { useDocsLocalization } from "./use-docs-localization";

const fallbackSpeakerLanguages = SPEAKER_CAPTION_LANGUAGES;
const fallbackTargetLanguages = TARGET_CAPTION_LANGUAGES;

type CatalogParser<TLanguage> = (value: unknown) => TLanguage[] | null;

function useWorkerLanguageCatalog<TLanguage>({
  path,
  fallback,
  parse,
}: {
  path: string;
  fallback: readonly TLanguage[];
  parse: CatalogParser<TLanguage>;
}) {
  const [languages, setLanguages] = useState<readonly TLanguage[]>(fallback);
  const [hasCurrentCatalog, setHasCurrentCatalog] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [refreshSequence, setRefreshSequence] = useState(0);

  useEffect(() => {
    const abortController = new globalThis.AbortController();
    setHasCurrentCatalog(false);
    setRefreshFailed(false);

    const refresh = async () => {
      try {
        const response = await fetchAppRequest(
          new URL(path, WORKER_ORIGIN).toString(),
          {
            credentials: "omit",
            headers: { Accept: "application/json" },
            signal: abortController.signal,
          },
        );
        if (!response.ok) {
          throw new Error(`Language catalog request failed: ${response.status}`);
        }

        const parsed = parse(await response.json());
        if (!parsed) throw new Error("Language catalog response was invalid");

        if (!abortController.signal.aborted) {
          setLanguages(parsed);
          setHasCurrentCatalog(true);
        }
      } catch {
        if (!abortController.signal.aborted) setRefreshFailed(true);
      }
    };

    void refresh();

    return () => abortController.abort();
  }, [parse, path, refreshSequence]);

  const retry = useCallback(() => {
    setRefreshSequence((current) => current + 1);
  }, []);

  return { hasCurrentCatalog, languages, refreshFailed, retry };
}

function normalizeSearchValue(value: string) {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase();
}

function useHasHydrated() {
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  return hasHydrated;
}

function SpeakerLanguageChips({
  hasHydrated,
  languages,
  locale,
}: {
  hasHydrated: boolean;
  languages: readonly PublicCaptionLanguage[];
  locale: ReturnType<typeof useDocsLocalization>["locale"];
}) {
  return (
    <ul className="kaleo-language-chips">
      {languages.map((language) => (
        <li className="kaleo-language-chip" key={language.code}>
          <span className="kaleo-language-emoji" aria-hidden="true">
            {language.emoji}
          </span>
          <span className="kaleo-language-english-name">
            {hasHydrated
              ? getLocalizedLanguageName(language.code, locale, language.name)
              : language.name}
          </span>
        </li>
      ))}
    </ul>
  );
}

function getAutomaticDetectionLanguages(
  languages: readonly PublicCaptionLanguage[],
): PublicCaptionLanguage[] {
  const languagesByCode = new Map(
    languages.map((language) => [language.code, language]),
  );
  const codes =
    languagesByCode.get(MULTILINGUAL_SPEAKER_LANGUAGE)
      ?.automaticDetectionLanguageCodes ?? [];

  return codes.flatMap((code) => {
    const language = languagesByCode.get(code);
    return language ? [language] : [];
  });
}

export function SpeakerLanguageCatalog() {
  const { locale, messages } = useDocsLocalization();
  const hasHydrated = useHasHydrated();
  const [query, setQuery] = useState("");
  const {
    languages: currentLanguages,
    refreshFailed,
    retry,
  } = useWorkerLanguageCatalog({
    path: "/api/speaker-languages",
    fallback: fallbackSpeakerLanguages,
    parse: parseSpeakerLanguageCatalog,
  });
  const speakerLanguages = useMemo(
    () =>
      currentLanguages.filter(
        (language) => language.code !== MULTILINGUAL_SPEAKER_LANGUAGE,
      ),
    [currentLanguages],
  );
  const filteredLanguages = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(query.trim());
    if (!normalizedQuery) return speakerLanguages;

    return speakerLanguages.filter((language) =>
      [
        language.name,
        hasHydrated
          ? getLocalizedLanguageName(language.code, locale, language.name)
          : language.name,
        language.code,
      ].some((value) =>
        normalizeSearchValue(value).includes(normalizedQuery),
      ),
    );
  }, [hasHydrated, locale, query, speakerLanguages]);

  return (
    <div className="kaleo-language-catalog">
      <div className="kaleo-language-search">
        <label className="kaleo-sr-only" htmlFor="speaker-language-search">
          {messages.speakerSearch}
        </label>
        <svg aria-hidden="true" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4-4" />
        </svg>
        <input
          id="speaker-language-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={messages.speakerSearch}
          autoComplete="off"
        />
        {query ? (
          <button
            className="kaleo-language-search-clear"
            type="button"
            aria-label={messages.clearSpeakerSearch}
            onClick={() => setQuery("")}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        ) : null}
      </div>

      <CatalogRefreshAlert
        messages={messages}
        refreshFailed={refreshFailed}
        retry={retry}
      />

      {filteredLanguages.length > 0 ? (
        <SpeakerLanguageChips
          hasHydrated={hasHydrated}
          languages={filteredLanguages}
          locale={locale}
        />
      ) : (
        <div className="kaleo-language-empty" role="status">
          <p>{messages.noSpeakerLanguages}</p>
          <button type="button" onClick={() => setQuery("")}>
            {messages.clearSearch}
          </button>
        </div>
      )}
    </div>
  );
}

export function MultilingualLanguageCatalog() {
  const { locale, messages } = useDocsLocalization();
  const hasHydrated = useHasHydrated();
  const {
    languages: currentLanguages,
    refreshFailed,
    retry,
  } = useWorkerLanguageCatalog({
    path: "/api/speaker-languages",
    fallback: fallbackSpeakerLanguages,
    parse: parseSpeakerLanguageCatalog,
  });
  const languages = useMemo(
    () => getAutomaticDetectionLanguages(currentLanguages),
    [currentLanguages],
  );

  return (
    <div className="kaleo-language-catalog kaleo-multilingual-language-catalog">
      <CatalogRefreshAlert
        messages={messages}
        refreshFailed={refreshFailed}
        retry={retry}
      />
      <SpeakerLanguageChips
        hasHydrated={hasHydrated}
        languages={languages}
        locale={locale}
      />
    </div>
  );
}

function LanguageSupportCheck({ label }: { label: string }) {
  return (
    <span className="kaleo-language-support-check" aria-label={label} role="img">
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="m5 12 4 4L19 6" />
      </svg>
    </span>
  );
}

export function TargetLanguageCatalog() {
  const { messages } = useDocsLocalization();
  const [query, setQuery] = useState("");
  const {
    hasCurrentCatalog,
    languages: targetLanguages,
    refreshFailed,
    retry,
  } = useWorkerLanguageCatalog({
    path: "/api/target-languages",
    fallback: fallbackTargetLanguages,
    parse: parseTargetLanguageCatalog,
  });

  const filteredLanguages = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(query.trim());
    if (!normalizedQuery) return targetLanguages;

    return targetLanguages.filter((language) =>
      [
        language.name,
        language.nativeName,
        language.code,
      ].some((value) =>
        normalizeSearchValue(value).includes(normalizedQuery),
      ),
    );
  }, [query, targetLanguages]);

  return (
    <div className="kaleo-language-catalog kaleo-target-language-catalog">
      <div className="kaleo-language-search">
        <label className="kaleo-sr-only" htmlFor="target-language-search">
          {messages.targetSearch}
        </label>
        <svg aria-hidden="true" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4-4" />
        </svg>
        <input
          id="target-language-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={messages.targetSearch}
          autoComplete="off"
        />
        {query ? (
          <button
            className="kaleo-language-search-clear"
            type="button"
            aria-label={messages.clearTargetSearch}
            onClick={() => setQuery("")}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        ) : null}
      </div>

      <CatalogRefreshAlert
        messages={messages}
        refreshFailed={refreshFailed}
        retry={retry}
      />

      {filteredLanguages.length > 0 ? (
        <div className="kaleo-language-table-wrap">
          <table className="kaleo-language-table">
            <thead>
              <tr>
                <th scope="col">{messages.languageName}</th>
                <th scope="col">{messages.languageCode}</th>
                <th scope="col">{messages.captionsAvailable}</th>
                {hasCurrentCatalog ? (
                  <th scope="col">{messages.speechAvailable}</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {filteredLanguages.map((language: PublicTargetCaptionLanguage) => {
                const { primaryName, secondaryName } =
                  getPublicCaptionLanguageDisplayNames(language);

                return (
                  <tr key={language.code}>
                    <th scope="row">
                      <span className="kaleo-table-language">
                        <span aria-hidden="true">{language.emoji}</span>
                        <span className="kaleo-table-language-names">
                          <span>{primaryName}</span>
                          {secondaryName ? (
                            <span className="kaleo-table-language-secondary-name">
                              {secondaryName}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </th>
                    <td>
                      <code>{language.code}</code>
                    </td>
                    <td>
                      <LanguageSupportCheck
                        label={formatSupportLabel(
                          messages,
                          "captionsAvailableFor",
                          primaryName,
                        )}
                      />
                    </td>
                    {hasCurrentCatalog ? (
                      <td>
                        {hasRemoteCaptionSpeech(language) ? (
                          <LanguageSupportCheck
                            label={formatSupportLabel(
                              messages,
                              "speechAvailableFor",
                              primaryName,
                            )}
                          />
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="kaleo-language-empty" role="status">
          <p>{messages.noTargetLanguages}</p>
          <button type="button" onClick={() => setQuery("")}>
            {messages.clearSearch}
          </button>
        </div>
      )}
    </div>
  );
}

function CatalogRefreshAlert({
  messages,
  refreshFailed,
  retry,
}: {
  messages: DocsMessages;
  refreshFailed: boolean;
  retry: () => void;
}) {
  if (!refreshFailed) return null;

  return (
    <div className="kaleo-language-catalog-alert" role="status">
      <span>{messages.languageCatalogUnavailable}</span>
      <button type="button" onClick={retry}>
        {messages.retryLanguageCatalog}
      </button>
    </div>
  );
}

function formatSupportLabel(
  messages: DocsMessages,
  key: "captionsAvailableFor" | "speechAvailableFor",
  language: string,
) {
  return formatDocsMessage(messages, key, { language });
}
